import { db } from '@/data/firebase/config';
import { useAuth } from '@/domain/context/auth';
import { Campaign, CampaignFormData } from '@/domain/types/campaigns';
import useCampaigns from '@/presentation/hooks/firebase/use-campaigns';
import { collection, doc, getDoc, getDocs, query } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const defaultForm: CampaignFormData = {
  name: '',
  nameEn: '',
  hashtag: '',
  instructions: '',
  instructionsEn: '',
  startDate: '',
  endDate: '',
  isActive: true,
};

const CampaignAddPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const { user } = useAuth();
  const { createCampaign, updateCampaign, getActiveCampaign } = useCampaigns();
  const [form, setForm] = useState<CampaignFormData>(defaultForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hashtagLocked, setHashtagLocked] = useState(false);
  const [conflictCampaign, setConflictCampaign] = useState<Campaign | null>(null);

  useEffect(() => {
    if (!editId) return;
    Promise.all([
      getDoc(doc(db, 'campaigns', editId)),
      getDocs(query(collection(db, 'campaigns', editId, 'rankings'))),
    ]).then(([snap, rankingsSnap]) => {
      if (!snap.exists()) return;
      const data = snap.data() as Omit<Campaign, 'id'>;
      setForm({
        name: data.name,
        nameEn: data.nameEn ?? '',
        hashtag: data.hashtag,
        instructions: data.instructions ?? (data as any).description ?? '',
        instructionsEn: data.instructionsEn ?? '',
        startDate: data.startDate.toDate().toISOString().split('T')[0],
        endDate: data.endDate.toDate().toISOString().split('T')[0],
        isActive: data.isActive,
      });
      setHashtagLocked(!rankingsSnap.empty);
    });
  }, [editId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const save = async () => {
    setIsSaving(true);
    setError(null);
    try {
      if (editId) {
        await updateCampaign(editId, form);
      } else {
        await createCampaign(form, user?.email ?? '');
      }
      navigate('/dashboard/campaigns');
    } catch {
      setError('Error al guardar la campaña');
    } finally {
      setIsSaving(false);
      setConflictCampaign(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.hashtag || !form.startDate || !form.endDate) {
      setError('Todos los campos obligatorios deben completarse');
      return;
    }
    if (form.isActive) {
      const active = await getActiveCampaign(editId ?? undefined);
      if (active) {
        setConflictCampaign(active);
        return;
      }
    }
    await save();
  };

  return (
    <div className="flex flex-col gap-4 p-4 max-w-lg">
      <h1 className="text-3xl font-bold">
        {editId ? 'Editar campaña' : 'Nueva campaña'}
      </h1>

      {error && <p className="text-red-500">{error}</p>}

      {/* Modal de confirmación campaña activa existente */}
      {conflictCampaign && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setConflictCampaign(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-lg text-gray-900">Campaña activa existente</h3>
            <p className="text-sm text-gray-600">
              La campaña <span className="font-semibold">"{conflictCampaign.name}"</span> está
              actualmente activa. Al continuar quedará inactiva y esta será la nueva campaña
              en curso. ¿Deseas continuar?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConflictCampaign(null)}
                className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={save}
                disabled={isSaving}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? 'Guardando...' : 'Sí, continuar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Nombre ES */}
        <div>
          <label className="font-medium block mb-1">Nombre *</label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            className="border rounded-lg px-3 py-2 w-full"
            placeholder="Seguridad de Clase Mundial"
          />
        </div>

        {/* Nombre EN */}
        <div>
          <label className="font-medium block mb-1">Name (English)</label>
          <input
            name="nameEn"
            value={form.nameEn}
            onChange={handleChange}
            className="border rounded-lg px-3 py-2 w-full"
            placeholder="World Class Safety"
          />
        </div>

        <div>
          <label className="font-medium block mb-1">Hashtag *</label>
          <input
            name="hashtag"
            value={form.hashtag}
            onChange={handleChange}
            disabled={hashtagLocked}
            className="border rounded-lg px-3 py-2 w-full disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            placeholder="#ClaseMundial"
          />
          {hashtagLocked ? (
            <p className="text-xs text-amber-600 mt-1">
              El hashtag no se puede cambiar porque la campaña ya tiene participantes.
            </p>
          ) : (
            <p className="text-xs text-gray-400 mt-1">
              El símbolo # se agrega automáticamente si no lo incluyes.
            </p>
          )}
        </div>

        {/* Instrucciones ES */}
        <div>
          <label className="font-medium block mb-1">Instrucciones</label>
          <textarea
            name="instructions"
            value={form.instructions}
            onChange={handleChange}
            className="border rounded-lg px-3 py-2 w-full"
            rows={3}
            placeholder="Escribe un comentario con #ClaseMundial para participar..."
          />
        </div>

        {/* Instrucciones EN */}
        <div>
          <label className="font-medium block mb-1">Instructions (English)</label>
          <textarea
            name="instructionsEn"
            value={form.instructionsEn}
            onChange={handleChange}
            className="border rounded-lg px-3 py-2 w-full"
            rows={3}
            placeholder="Write a comment with #ClaseMundial to participate..."
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <label className="font-medium block mb-1">Fecha inicio *</label>
            <input
              type="date"
              name="startDate"
              value={form.startDate}
              onChange={handleChange}
              className="border rounded-lg px-3 py-2 w-full"
            />
          </div>
          <div className="flex-1">
            <label className="font-medium block mb-1">Fecha fin *</label>
            <input
              type="date"
              name="endDate"
              value={form.endDate}
              onChange={handleChange}
              className="border rounded-lg px-3 py-2 w-full"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="isActive"
            checked={form.isActive}
            onChange={handleChange}
            className="w-4 h-4"
          />
          <span className="font-medium">Campaña activa</span>
        </label>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={isSaving}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? 'Guardando...' : 'Guardar'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/dashboard/campaigns')}
            className="border px-6 py-2 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default CampaignAddPage;
